import React, { useState } from 'react';
import { Tabs, Tab } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import TabPanel from '@components/layout/tabs/TabPanel';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { modifyPerson } from './queries';
import { ValueOf } from '@utils/helper-utils';
import {
  validateBeneficiaryForm,
  validateBeneficiaryField,
} from './validation';
import EnhancedInternationalPhoneInput from '@components/_old/form/EnhancedInternationalPhoneInput';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import { IAbstractRecord } from '@models';
import DateService from '@services/DateService';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import { IPersonWidgetInfo } from '..';

interface IBeneficiaryPersonPopUpFormProps {
  lineExternalCode: string;
  data: IPersonWidgetInfo[];
  lovs: Record<string, Record<string, string>>;
  onClose: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles()(() => ({
  content: {
    padding: '0 63px 40px 30px',
  },
  tabsRoot: {
    minHeight: 'auto',
    borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    marginBottom: '20px',
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: 'auto',
    padding: '6px 12px',
    marginRight: '4px',
    color: MAIN_THEME.color.black47,
    fontFamily: MAIN_THEME.font.regular,
    '&.Mui-selected': {
      color: MAIN_THEME.color.black,
      fontFamily: MAIN_THEME.font.medium,
      backgroundColor: MAIN_THEME.color.white98,
    },
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 23.5%)',
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
  },
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '15px',
    marginTop: '3em',
  },
}));

const BeneficiaryPersonPopUpForm: React.FC<
  IBeneficiaryPersonPopUpFormProps
> = ({ lineExternalCode, data, lovs, onClose, onSuccess }) => {
  const { classes } = useStyles();
  const params = useParams();

  const [modifyPersonAction] = useMutation(modifyPerson());

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [policyPersons, setPolicyPersons] = useState<IPersonWidgetInfo[]>(data);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>(
    {}
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleFieldUpdate = (
    index: number,
    fieldName: keyof IPersonWidgetInfo,
    value: ValueOf<IPersonWidgetInfo>
  ) => {
    const newPersons = [...policyPersons];
    (newPersons[index] as IAbstractRecord)[fieldName] = value;
    setPolicyPersons(newPersons);
  };

  const handleFieldBlur = (
    index: number,
    fieldName: keyof IPersonWidgetInfo
  ) => {
    const value = policyPersons[index][fieldName];
    const errorMessage = validateBeneficiaryField(fieldName, value);
    setErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [fieldName]: errorMessage },
    }));
  };

  const handleSubmit = async () => {
    const { valid, errors: validationErrors } = validateBeneficiaryForm(
      policyPersons,
      lineExternalCode
    );
    setErrors(validationErrors);
    if (!valid) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
      return;
    }
    const personDetailsInputs = policyPersons.map((person) => ({
      type: 'BENEFICIARY',
      percentage: person.percentage,
      fullName: person.fullName,
      phoneType: person.phoneType.toUpperCase(),
      phoneNumber: person.phoneNumber,
      isTaxable: person.isTaxable,
      occupationClass: person.occupationClassCode,
      profession: person.profession,
      country: person.countryCode,
      zone: person.zone,
      city: person.city,
      street: person.street,
      building: person.building,
      addressDetails: person.addressDetails,
      addressDetailsArabic: person.addressDetailsArabic,
      dateOfBirth: DateService.formatDateBackend(person.dateOfBirth),
      personCode: person.personCode || null,
    }));
    try {
      await modifyPersonAction({
        variables: {
          entityId: params.id,
          personDetailsInputs,
        },
      });
      toast.success(
        <ToastSuccessMessage>Policy updated successfully!</ToastSuccessMessage>
      );
      onSuccess();
      onClose();
    } catch (error) {
      toast(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    }
  };

  const currentPerson = policyPersons[selectedTab];

  return (
    <div className={classes.content}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Beneficiary Person Tabs"
        classes={{ root: classes.tabsRoot }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {policyPersons.map((_, index) => (
          <Tab
            key={`Beneficiary-${index}`}
            label={`Beneficiary ${index + 1}`}
            classes={{ root: classes.tabRoot }}
          />
        ))}
      </Tabs>
      <TabPanel index={selectedTab} key={`beneficiary-${selectedTab}`}>
        <WidgetSection title="General Details">
          <div className={classes.fieldRow}>
            <TextInputFormField
              name={`fullName-${selectedTab}`}
              title="Full Name*"
              placeholder="Full Name"
              value={currentPerson.fullName || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'fullName', e.target.value)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'fullName')}
              error={errors[selectedTab]?.fullName || ''}
              disabled
            />
            <DatePickerFormField
              name={`dateOfBirth-${selectedTab}`}
              title={
                ['43', '6', '19', '48'].includes(lineExternalCode)
                  ? 'Date of Birth*'
                  : 'Date of Birth'
              }
              value={currentPerson.dateOfBirth}
              onDateChange={(date) =>
                handleFieldUpdate(
                  selectedTab,
                  'dateOfBirth',
                  date ? date.toISOString() : ''
                )
              }
              onBlur={() => handleFieldBlur(selectedTab, 'dateOfBirth')}
              error={errors[selectedTab]?.dateOfBirth || ''}
              maxDate={new Date()}
              canClearDate={false}
              disabled
            />
            <SelectFormField
              name={`phoneType-${selectedTab}`}
              title="Phone Type"
              placeholder="Phone Type"
              value={(currentPerson.phoneType || '').toUpperCase()}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'phoneType', val)
              }
              selectOptions={lovs.phoneType || {}}
            />
            <EnhancedInternationalPhoneInput
              name="phoneNumber"
              title="Phone Number*"
              placeholder="Phone Number"
              onChange={(val) =>
                handleFieldUpdate(selectedTab, 'phoneNumber', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'phoneNumber')}
              value={currentPerson.phoneNumber || ''}
              error={errors[selectedTab]?.phoneNumber || ''}
              disableDropDown={false}
              editCountryCode={false}
              defaultCountry="lb"
            />
            <SelectFormField
              name={`nationalityOne-${selectedTab}`}
              title="Nationality"
              placeholder="Nationality"
              value={currentPerson.nationalityOneCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'nationalityOneCode', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'nationalityOneCode')}
              error={errors[selectedTab]?.nationalityOneCode || ''}
              selectOptions={lovs.nationalities || {}}
            />
          </div>
        </WidgetSection>
        <WidgetSection title="Business Details">
          <div className={classes.fieldRow}>
            <SwitchFormField
              key={`taxable-${selectedTab}`}
              name={`taxable-${selectedTab}`}
              title="Taxable"
              value={currentPerson.isTaxable}
              onChange={(_, val) =>
                handleFieldUpdate(selectedTab, 'isTaxable', val)
              }
            />
            <SelectFormField
              name={`occupationClass-${selectedTab}`}
              title="Occupation Class"
              placeholder="Occupation Class"
              value={currentPerson.occupationClassCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'occupationClassCode', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'occupationClassCode')}
              error={errors[selectedTab]?.occupationClassCode || ''}
              selectOptions={lovs.occupationClasses || {}}
            />
            <TextInputFormField
              name={`profession-${selectedTab}`}
              title="Profession"
              placeholder="Profession"
              value={currentPerson.profession || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'profession', e.target.value)
              }
            />
          </div>
        </WidgetSection>
        <WidgetSection title="Address Details">
          <div className={classes.fieldRow}>
            <SelectFormField
              name={`country-${selectedTab}`}
              title="Country*"
              placeholder="Country"
              value={currentPerson.countryCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'countryCode', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'countryCode')}
              error={errors[selectedTab]?.countryCode || ''}
              selectOptions={lovs.countries || {}}
            />
            <SelectFormField
              name={`zone-${selectedTab}`}
              title="Zone"
              placeholder="Zone"
              value={currentPerson.zone || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'zone', val)
              }
              selectOptions={lovs.zones || {}}
            />
            <TextInputFormField
              name={`city-${selectedTab}`}
              title="City*"
              placeholder="City"
              value={currentPerson.city || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'city', e.target.value)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'city')}
              error={errors[selectedTab]?.city || ''}
            />
            <TextInputFormField
              name={`street-${selectedTab}`}
              title="Street"
              placeholder="Street"
              value={currentPerson.street || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'street', e.target.value)
              }
            />
            <TextInputFormField
              name={`building-${selectedTab}`}
              title="Building"
              placeholder="Building"
              value={currentPerson.building || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'building', e.target.value)
              }
            />
            <TextInputFormField
              name={`addressDetails-${selectedTab}`}
              title="Address Details"
              placeholder="Address Details"
              value={currentPerson.addressDetails || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'addressDetails', e.target.value)
              }
            />
            <TextInputFormField
              name={`addressDetailsArabic-${selectedTab}`}
              title="Address Details Arabic"
              placeholder="Address Details Arabic"
              value={currentPerson.addressDetailsArabic || ''}
              onChange={(e) =>
                handleFieldUpdate(
                  selectedTab,
                  'addressDetailsArabic',
                  e.target.value
                )
              }
            />
          </div>
        </WidgetSection>
      </TabPanel>
      <div className={classes.buttonsContainer}>
        <EnhancedButton onClick={onClose}>Cancel</EnhancedButton>
        <EnhancedButton isPrimary onClick={handleSubmit}>
          Submit
        </EnhancedButton>
      </div>
    </div>
  );
};

export default BeneficiaryPersonPopUpForm;
